import React from "react";
import { Sparkle, Gift, X } from "@phosphor-icons/react";

/**
 * Celebrates the user's first-chat cashback win:
 *   • Eyebrow: "🎁 You won a special prize"
 *   • Headline: "100% cashback unlocked"
 *   • CTA: "Recharge ₹100 → get ₹150" (the bonus auto-applies on next top-up)
 *
 * Visible only when `open === true`. Props:
 *   - amount: bonus amount in INR (₹X)
 *   - onRecharge(): open the wallet top-up modal (parent decides flow)
 *   - onClose(): dismiss without recharging
 */
export default function CashbackCelebrationModal({ open, amount = 50, onRecharge, onClose }) {
  if (!open) return null;
  const bonus = Math.max(0, Math.round(Number(amount) || 0));
  const exampleRecharge = 100;
  const exampleTotal = exampleRecharge + bonus;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70"
      data-testid="cashback-celebration-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-w-md w-full rounded-2xl overflow-hidden shadow-2xl">
        {/* Animated cosmic gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0f2e] via-[#3b1a5b] to-[#FFB800] opacity-95" />
        <div className="relative px-6 py-7 sm:px-8 sm:py-8 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition"
            data-testid="cashback-modal-close"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>

          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-16 h-16 rounded-full bg-[#FFB800] flex items-center justify-center shadow-lg ring-4 ring-white/20">
              <Gift size={32} weight="fill" className="text-[#3F2D1B]" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-200 font-semibold mt-1">
              You won a special prize
            </p>
            <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight">
              Congratulations — you unlocked<br />
              <span className="text-[#FFD66E]">₹{bonus} cashback</span>
            </h2>
            <p className="text-sm text-amber-50/90 leading-snug">
              100% cashback (up to ₹{bonus}) on your first chat is now waiting in your account. It auto-applies on your next wallet recharge.
            </p>

            {/* The math callout */}
            <div className="mt-4 w-full rounded-xl bg-white/10 backdrop-blur px-4 py-3 border border-white/15">
              <div className="flex items-center justify-between text-sm">
                <span className="text-amber-100/90">Recharge</span>
                <span className="font-mono font-semibold">₹{exampleRecharge}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-amber-100/90 flex items-center gap-1">
                  <Sparkle size={12} weight="fill" className="text-[#FFD66E]" />
                  Bonus
                </span>
                <span className="font-mono font-semibold text-[#FFD66E]">+ ₹{bonus}</span>
              </div>
              <div className="h-px bg-white/20 my-2" />
              <div className="flex items-center justify-between text-base">
                <span className="font-semibold">You get</span>
                <span className="font-mono font-bold text-lg">₹{exampleTotal}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={onRecharge}
              className="mt-5 w-full rounded-full bg-[#FFB800] hover:bg-[#FFC933] active:bg-[#E5A600] text-[#1A1410] font-semibold py-3 px-6 transition shadow-lg"
              data-testid="cashback-recharge-btn"
            >
              Recharge ₹{exampleRecharge} & claim ₹{bonus} bonus
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-widest text-amber-100/80 hover:text-white mt-1 py-2"
              data-testid="cashback-later-btn"
            >
              I&apos;ll claim later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
